import { AntDesign } from "@expo/vector-icons";
import { useFocusEffect } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { Button, Page, TextButton } from "@towersystems/roam-common-native";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { ROAM_LOCATION_HEADER_KEY } from "../../../../constants";
import { ProductsRootScreenParamList } from "../../../../types";
import { useActiveLocation } from "../../../../utilities/app-state/hooks";
import { useTranslation } from "../../../../utilities/i18n";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import { useLoadMoreList } from "../../../../utilities/load-more-list";
import { useScheduler } from "../../../../utilities/scheduler";
import { SearchContext, useSearch } from "../../../../utilities/search";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { productFilterValuesToApiInput } from "../../common";
import { ProductResourceList } from "../../components/ProductResourceList/ProductResourceList";
import { ProductsFilterValues } from "../../types";
import { QUERY_PRODUCT_LIST } from "../../utils";

export type ProductsListScreenProps = StackScreenProps<
  ProductsRootScreenParamList,
  "ProductsListScreen"
>;

export const ProductsListScreen = ({ navigation }: ProductsListScreenProps) => {
  const { t } = useTranslation("product");
  const { location, locationId } = useActiveLocation();
  const search = useSearch();
  const { schedule } = useScheduler(500);
  const transitionEnded = useHasTransitionEnded(navigation);

  const { filterState, watch } = useListFilterContext<ProductsFilterValues>();
  const { activeFiltersCount } = filterState;

  const watchedFilterValues = watch();

  const loadMoreList = useLoadMoreList<Product>({
    initialLoad: false,
    query: QUERY_PRODUCT_LIST,
    resultKey: "products",
    sort: { updatedAt: "DESC" },
    context: {
      headers: {
        [ROAM_LOCATION_HEADER_KEY]: locationId ?? undefined,
      },
    },
    variableFormatter: (variables) => {
      return {
        options: {
          ...variables,
          filter: {
            ...variables.filter,
            internal: {
              eq: false,
            },
          },
        },
      };
    },
  });

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label={t("button.new_product")}
          isAction
          onPress={() =>
            navigation.navigate("CreateProductRootScreen", {
              defaultValues: {
                locationId: location?.id,
              },
              screen: "CreateProductScreen",
            })
          }
          size="compact"
        ></Button>
      ),
    });
  }, [location]);

  useFocusEffect(
    React.useCallback(() => {
      navigation.setOptions({
        headerTitle() {
          return (
            <TextButton
              iconStyle={{ marginRight: 0 }}
              icon={<AntDesign name="filter" size={20} />}
              onPress={() => {
                navigation.navigate("ProductListFilterRootScreen", {
                  screen: "ProductListFilterScreen",
                });
              }}
            >
              {t("common:filter", {
                count: activeFiltersCount,
              })}
            </TextButton>
          );
        },
      });
    }, [activeFiltersCount])
  );

  React.useEffect(() => {
    if (loadMoreList.called) {
      schedule(() => handleLoadMore(true));
    }
  }, [search.query]);

  const handleLoadMore = (reset?: boolean) => {
    loadMoreList.loadMore(
      {
        filter: {
          term: { contains: search.query },
          ...productFilterValuesToApiInput(watchedFilterValues),
        },
      },
      reset
    );
  };

  useFocusEffect(
    React.useCallback(() => {
      handleLoadMore(true);
    }, [watchedFilterValues])
  );

  const items: Product[] = loadMoreList.items || [];

  if (
    (loadMoreList.loading &&
      loadMoreList.items.length == 0 &&
      !search.query.length) ||
    false == transitionEnded
  ) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />
      </Page>
    );
  }

  return (
    <SearchContext.Provider value={search}>
      <Page scrollable={false} horizontalPadding={0} fullWidth>
        <ProductResourceList
          items={items}
          onEndReached={() => handleLoadMore(false)}
          onItemPress={(item) =>
            navigation.navigate("EditProductRootScreen", {
              productId: item.id,
              screen: "EditProductScreen",
            })
          }
        />
      </Page>
    </SearchContext.Provider>
  );
};
